import { useEffect, FormEvent, useState } from 'react';
import { useRevealOnScroll } from '@/hooks/useScrollReveal';

const CONTACT_URL = 'https://readdy.ai/api/form/d76i815r5g02gnd6a190';
const QUOTE_URL = 'https://readdy.ai/api/form/d76i815r5g02gnd6a19g';

const infos = [
  { icon: 'ri-phone-line', label: 'Destek Hattı', value: '0850 242 0 822', href: 'tel:+908502420822', color: '#E8611A' },
  { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '0505 891 6 367', href: 'https://wa.me/905058916367', color: '#16A34A' },
  { icon: 'ri-mail-line', label: 'E-posta', value: 'cs@cargonet.com.tr', href: 'mailto:cs@cargonet.com.tr', color: '#E8611A' },
  { icon: 'ri-map-pin-2-line', label: 'Adres', value: 'Turgut Özal Mah. 55.Sk. No:47/3 Esenyurt/İSTANBUL', href: '#', color: '#E8611A' },
  { icon: 'ri-time-line', label: 'Çalışma Saatleri', value: 'Pazartesi – Cumartesi: 08:00 – 20:00', href: '#', color: '#E8611A' },
];

type Tab = 'contact' | 'quote';

export default function ContactPage() {
  useRevealOnScroll();
  useEffect(() => {
    document.title = 'İletişim | CargoNet – Ücretsiz Lojistik Danışmanlığı';
    window.scrollTo(0, 0);
  }, []);

  const [tab, setTab] = useState<Tab>('contact');
  const [cSent, setCsent] = useState(false);
  const [qSent, setQsent] = useState(false);
  const [cForm, setCform] = useState({ name: '', email: '', phone: '', message: '' });
  const [qForm, setQform] = useState({ name: '', email: '', phone: '', from: '', to: '', weight: '', service: '', note: '' });

  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 700, color: 'var(--text3)',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    display: 'block', marginBottom: 8,
  };

  const handleContact = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(cForm as Record<string, string>).toString(),
      });
    } catch { /* noop */ }
    setCsent(true);
  };

  const handleQuote = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await fetch(QUOTE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(qForm as Record<string, string>).toString(),
      });
    } catch { /* noop */ }
    setQsent(true);
  };

  return (
    <main style={{ background: 'var(--bg)', color: 'var(--text1)' }}>

      {/* ── HERO ── */}
      <section className="relative pt-36 pb-20 overflow-hidden" style={{ background: 'var(--bg)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(var(--dot-color) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div style={{ position: 'absolute', top: '-200px', right: '-150px', width: 600, height: 600, background: 'radial-gradient(circle, var(--glow2) 0%, transparent 65%)', borderRadius: '50%' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label"><div className="label-dot" />İletişim</div>
          <h1
            className="h-font leading-[0.95] mb-5"
            style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 'clamp(46px,6vw,78px)', color: 'var(--text1)' }}
          >
            Konuşalım.<br /><span className="text-gradient">Hemen Başlayalım.</span>
          </h1>
          <p style={{ fontSize: 18, color: 'var(--text2)', maxWidth: 480, lineHeight: 1.75 }}>
            Ücretsiz danışmanlık için bizimle iletişime geçin. Ekibimiz 24 saat içinde size dönecektir.
          </p>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section style={{ background: 'var(--bg2)', padding: '80px 0', borderTop: '1px solid var(--border)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* ── LEFT: Info ── */}
            <div>
              <h2
                className="reveal"
                style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 22, color: 'var(--text1)', marginBottom: 20 }}
              >
                İletişim Bilgileri
              </h2>
              <div className="space-y-3 mb-8">
                {infos.map((info, i) => (
                  <a
                    key={i}
                    href={info.href}
                    rel="nofollow"
                    className={`reveal d${i + 1} flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300`}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', textDecoration: 'none' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(232,97,26,0.07)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,97,26,0.25)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'var(--surface)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                    }}
                  >
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: `${info.color}18`, border: `1px solid ${info.color}30` }}
                    >
                      <i className={`${info.icon}`} style={{ color: info.color }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{info.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)', lineHeight: 1.5 }}>{info.value}</div>
                    </div>
                  </a>
                ))}
              </div>

              {/* Map */}
              <div
                className="reveal d6 rounded-2xl overflow-hidden"
                style={{ height: 200, border: '1px solid var(--border)' }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.5!2d28.6750!3d41.0193!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAxJzA5LjUiTiAyOMKwNDAnMzAuMCJF!5e0!3m2!1str!2str!4v1620000000000!5m2!1str!2str"
                  width="100%"
                  height="200"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(200deg)' }}
                  allowFullScreen
                  loading="lazy"
                  title="CargoNet Harita"
                />
              </div>
            </div>

            {/* ── RIGHT: Forms ── */}
            <div className="lg:col-span-2 reveal-r">
              {/* Tab switcher */}
              <div
                className="flex gap-2 mb-8 p-1 rounded-2xl"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 'fit-content' }}
              >
                {(['contact', 'quote'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className="px-6 py-2.5 rounded-xl text-sm cursor-pointer transition-all duration-300 whitespace-nowrap"
                    style={{
                      fontWeight: 700,
                      background: tab === t ? 'var(--orange)' : 'transparent',
                      color: tab === t ? '#fff' : 'var(--text2)',
                      boxShadow: tab === t ? '0 0 20px rgba(232,97,26,0.3)' : 'none',
                    }}
                  >
                    {t === 'contact' ? (
                      <><i className="ri-message-3-line mr-2" />İletişim Formu</>
                    ) : (
                      <><i className="ri-price-tag-3-line mr-2" />Teklif Al</>
                    )}
                  </button>
                ))}
              </div>

              {/* Contact form */}
              {tab === 'contact' && (
                cSent ? (
                  <div
                    className="text-center py-20 rounded-3xl"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                  >
                    <div
                      className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5"
                      style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}
                    >
                      <i className="ri-check-double-line text-2xl text-green-400" />
                    </div>
                    <h3 style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 24, color: 'var(--text1)', marginBottom: 10 }}>Mesajınız Alındı!</h3>
                    <p style={{ color: 'var(--text2)', fontSize: 15 }}>24 saat içinde size dönüş yapacağız.</p>
                  </div>
                ) : (
                  <form data-readdy-form id="contact-form" onSubmit={handleContact} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label style={labelStyle}>Ad Soyad *</label>
                        <input
                          name="name" type="text" required
                          value={cForm.name}
                          onChange={(e) => setCform({ ...cForm, name: e.target.value })}
                          placeholder="Adınız Soyadınız"
                          className="inp"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Telefon</label>
                        <input
                          name="phone" type="tel"
                          value={cForm.phone}
                          onChange={(e) => setCform({ ...cForm, phone: e.target.value })}
                          placeholder="0555 000 00 00"
                          className="inp"
                        />
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>E-posta *</label>
                      <input
                        name="email" type="email" required
                        value={cForm.email}
                        onChange={(e) => setCform({ ...cForm, email: e.target.value })}
                        placeholder="ornek@sirket.com"
                        className="inp"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Mesajınız *</label>
                      <textarea
                        name="message" required
                        value={cForm.message}
                        onChange={(e) => { if (e.target.value.length <= 500) setCform({ ...cForm, message: e.target.value }); }}
                        placeholder="Merhaba, lojistik ihtiyacım hakkında bilgi almak istiyorum..."
                        rows={5}
                        className="inp"
                        style={{ resize: 'none' }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', marginTop: 4 }}>{cForm.message.length}/500</div>
                    </div>
                    <button type="submit" className="btn-primary w-full justify-center cursor-pointer">
                      Gönder <i className="ri-send-plane-2-line bounce-x" />
                    </button>
                  </form>
                )
              )}

              {/* Quote form */}
              {tab === 'quote' && (
                qSent ? (
                  <div
                    className="text-center py-20 rounded-3xl"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                  >
                    <div
                      className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5"
                      style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}
                    >
                      <i className="ri-check-double-line text-2xl text-green-400" />
                    </div>
                    <h3 style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 24, color: 'var(--text1)', marginBottom: 10 }}>Teklifiniz Alındı!</h3>
                    <p style={{ color: 'var(--text2)', fontSize: 15 }}>24 saat içinde size özel teklif hazırlıyoruz.</p>
                  </div>
                ) : (
                  <form data-readdy-form id="quote-form" onSubmit={handleQuote} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label style={labelStyle}>Ad Soyad *</label>
                        <input
                          name="name" type="text" required
                          value={qForm.name}
                          onChange={(e) => setQform({ ...qForm, name: e.target.value })}
                          placeholder="Ad Soyad"
                          className="inp"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>E-posta *</label>
                        <input
                          name="email" type="email" required
                          value={qForm.email}
                          onChange={(e) => setQform({ ...qForm, email: e.target.value })}
                          placeholder="e-posta adresiniz"
                          className="inp"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label style={labelStyle}>Nereden</label>
                        <input
                          name="from" type="text"
                          value={qForm.from}
                          onChange={(e) => setQform({ ...qForm, from: e.target.value })}
                          placeholder="Çıkış şehri / ülkesi"
                          className="inp"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Nereye</label>
                        <input
                          name="to" type="text"
                          value={qForm.to}
                          onChange={(e) => setQform({ ...qForm, to: e.target.value })}
                          placeholder="Varış şehri / ülkesi"
                          className="inp"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label style={labelStyle}>Desi / Ağırlık</label>
                        <input
                          name="weight" type="text"
                          value={qForm.weight}
                          onChange={(e) => setQform({ ...qForm, weight: e.target.value })}
                          placeholder="Kg veya Desi"
                          className="inp"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Hizmet Türü</label>
                        <select
                          name="service"
                          value={qForm.service}
                          onChange={(e) => setQform({ ...qForm, service: e.target.value })}
                          className="inp"
                          style={{ cursor: 'pointer' }}
                        >
                          <option value="">Seçiniz</option>
                          <option>Ekspres Kurye</option>
                          <option>E-Ticaret Lojistiği</option>
                          <option>Kargo Taşımacılığı</option>
                          <option>Fulfillment</option>
                          <option>Mikro İhracat</option>
                          <option>A.TR Belgesi</option>
                          <option>On Board Kurye</option>
                          <option>Kargo Sigortası</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Notlar</label>
                      <textarea
                        name="note"
                        value={qForm.note}
                        onChange={(e) => { if (e.target.value.length <= 500) setQform({ ...qForm, note: e.target.value }); }}
                        placeholder="Kargo içeriği, özel talepler, ek bilgiler..."
                        rows={4}
                        className="inp"
                        style={{ resize: 'none' }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', marginTop: 4 }}>{qForm.note.length}/500</div>
                    </div>
                    <button type="submit" className="btn-primary w-full justify-center cursor-pointer">
                      Teklif İste <i className="ri-arrow-right-line bounce-x" />
                    </button>
                  </form>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── QUICK CONTACT STRIP ── */}
      <section style={{ background: 'var(--bg)', padding: '64px 20px', borderTop: '1px solid var(--border)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <a
              href="tel:+908502420822"
              rel="nofollow"
              className="reveal d1 flex items-center gap-4 p-6 rounded-2xl cursor-pointer transition-all duration-300"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,97,26,0.3)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: 'rgba(232,97,26,0.12)', border: '1px solid rgba(232,97,26,0.2)' }}>
                <i className="ri-phone-line text-[#E8611A] text-xl" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Destek Hattı</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text1)' }}>0850 242 0 822</div>
              </div>
            </a>
            <a
              href="https://wa.me/905058916367"
              rel="nofollow"
              className="reveal d2 flex items-center gap-4 p-6 rounded-2xl cursor-pointer transition-all duration-300"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(22,163,74,0.3)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: 'rgba(22,163,74,0.12)', border: '1px solid rgba(22,163,74,0.2)' }}>
                <i className="ri-whatsapp-line text-green-500 text-xl" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>WhatsApp</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text1)' }}>0505 891 6 367</div>
              </div>
            </a>
            <a
              href="mailto:cs@cargonet.com.tr"
              rel="nofollow"
              className="reveal d3 flex items-center gap-4 p-6 rounded-2xl cursor-pointer transition-all duration-300"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,97,26,0.3)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: 'rgba(232,97,26,0.12)', border: '1px solid rgba(232,97,26,0.2)' }}>
                <i className="ri-mail-line text-[#E8611A] text-xl" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>E-posta</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)' }}>cs@cargonet.com.tr</div>
              </div>
            </a>
          </div>
        </div>
      </section>

    </main>
  );
}
